'use client';

import { PortableText } from '@portabletext/react';
import { Search } from './Search';
import { ProductCategoryData } from './getData';
import { ProductCategories } from '@/components/productCategories';
import { ContentBlocksRenderer } from '@/components/contentBlocks/ContentBlocksRenderer';
import clsx from 'clsx';
import { useRestorePosition } from '@/hooks/useRestorePosition';
import { PageHeader } from '@/components/layout/PageHeader';

type CommonProps = {
  preview: boolean;
  previewData: any | null;
  slugs: string[] | null;
};

type ProductCategoryProps = ProductCategoryData & CommonProps;

export function CataloguePageWithProductListing({
  breadcrumbs,
  productCategories,
  sanityProductCategoryEnrichment,
  searchProps,
  slugs,
  relatedArticles,
}: ProductCategoryProps) {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    throw new Error('Missing breadcrumbs');
  }

  useRestorePosition();

  const fallbackTitle = breadcrumbs.slice(-1)[0]?.name || 'Ukjent';

  const title =
    sanityProductCategoryEnrichment?.title ||
    (breadcrumbs.length > 1 && breadcrumbs.slice(-1)[0]?.name) ||
    fallbackTitle;

  const subtitle = sanityProductCategoryEnrichment?.subtitle;
  const description = sanityProductCategoryEnrichment?.description;
  const contentBlocks = sanityProductCategoryEnrichment?.contentBlocks;
  const priorityContentBlocks =
    sanityProductCategoryEnrichment?.priorityContentBlocks;

  return (
    <>
      <PageHeader breadcrumbs={breadcrumbs} />
      <div className="space-y-5">
        <h1 className="mb-2 hd-3xl">{title}</h1>
        {subtitle && <p className="leading-6">{subtitle}</p>}
        {description && (
          <div className="leading-6 max-w-3xl">
            <PortableText value={description} />
          </div>
        )}

        <ProductCategories categories={productCategories} />

        {priorityContentBlocks && (
          <div
            className={clsx('grid gap-5', {
              'sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2':
                priorityContentBlocks.length === 2 ||
                priorityContentBlocks.length === 4,
              'sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3':
                priorityContentBlocks.length === 3,
            })}
          >
            <ContentBlocksRenderer
              blocks={priorityContentBlocks}
              getSize={() => 'md'}
            />
          </div>
        )}

        <Search
          {...searchProps}
          contentBlocks={contentBlocks}
          relatedArticles={relatedArticles}
          sanityProductCategoryEnrichment={sanityProductCategoryEnrichment}
        />
      </div>
    </>
  );
}

export default CataloguePageWithProductListing;
